import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, ClipboardList, Award, FileBarChart,
  BookOpen, Brain, LogOut, Menu, X, Sparkles, Bell,
  LayoutGrid, HelpCircle, Trophy, CalendarCheck, Megaphone, TrendingUp, Zap, Flame
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../api';

const NAV = [
  { icon: LayoutDashboard, label: 'Dashboard',     path: '/student/dashboard',       color: 'text-blue-300' },
  { icon: ClipboardList,   label: 'Assignments',   path: '/student/assignments',     color: 'text-amber-400' },
  { icon: Award,           label: 'My Results',    path: '/student/results',         color: 'text-emerald-400' },
  { icon: FileBarChart,    label: 'Report Card',   path: '/student/report-card',     color: 'text-sky-400' },
  { icon: CalendarCheck,   label: 'Attendance',    path: '/student/attendance',      color: 'text-teal-400' },
  { icon: Megaphone,       label: 'Notices',       path: '/student/announcements',   color: 'text-rose-400' },
  { icon: BookOpen,        label: 'Lesson Notes',  path: '/student/lessons',         color: 'text-indigo-400' },
  { icon: LayoutGrid,      label: 'Games',         path: '/student/games',           color: 'text-purple-400', module: 'games' },
  { icon: HelpCircle,      label: 'Quiz Zone',     path: '/student/quiz',            color: 'text-pink-400',   module: 'quiz_exam' },
  { icon: Trophy,          label: 'Leaderboard',   path: '/student/leaderboard',     color: 'text-amber-300' },
  { icon: Zap,             label: 'Exam Prep',     path: '/student/exam-prep',       color: 'text-orange-400', module: 'quiz_exam' },
  { icon: TrendingUp,      label: 'My Growth',     path: '/student/academic-growth', color: 'text-cyan-400' },
  { icon: Brain,           label: 'AI Tutor',      path: '/student/ai',              color: 'text-fuchsia-400' },
];

const LEVEL_COLORS = {
  emerald: { bg: 'bg-emerald-500', ring: 'ring-emerald-300' },
  blue:    { bg: 'bg-blue-500',    ring: 'ring-blue-300'    },
  amber:   { bg: 'bg-amber-500',   ring: 'ring-amber-300'   },
  orange:  { bg: 'bg-orange-500',  ring: 'ring-orange-300'  },
  purple:  { bg: 'bg-purple-600',  ring: 'ring-purple-300'  },
};

export default function StudentLayout() {
  const [open,       setOpen]      = useState(false);
  const [xpData,     setXpData]    = useState(null);
  const [streak,     setStreak]    = useState(0);
  const { user, school, logout } = useAuth();
  const navigate = useNavigate();

  const firstName = user?.first_name || 'Student';
  const lastName  = user?.last_name  || '';
  const initials  = `${firstName[0] || ''}${lastName[0] || ''}`.toUpperCase() || 'S';
  const fullName  = `${firstName} ${lastName}`.trim();

  useEffect(() => {
    api.get('/student/xp').then(r => { if (r?.success) setXpData(r.data); }).catch(() => {});
    api.get('/exam-prep/streak').then(r => { if (r?.success) setStreak(r.data?.streak || 0); }).catch(() => {});
  }, []);

  const levelColors = xpData ? (LEVEL_COLORS[xpData.level_color] || LEVEL_COLORS.emerald) : null;

  return (
    <div className="min-h-screen bg-[#EFF6FF] flex">
      {open && (
        <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setOpen(false)} />
      )}

      {/* ── Floating Sidebar ── */}
      <aside className={`
        fixed lg:sticky top-0 left-0 z-40 lg:z-auto
        h-screen flex-shrink-0
        transition-transform duration-300
        ${open ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div
          className="m-3 w-[248px] rounded-[28px] flex flex-col overflow-hidden shadow-2xl"
          style={{
            background: 'linear-gradient(180deg, #2563eb 0%, #1d58d4 50%, #1e40af 100%)',
            boxShadow: '0 20px 60px rgba(37, 99, 235, 0.35)',
            height: 'calc(100vh - 24px)',
          }}
        >
          {/* Brand row */}
          <div className="flex items-center justify-between px-5 pt-6 pb-3">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 bg-white/15 rounded-xl flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="text-white font-black text-sm tracking-wider">EDUVA</p>
                <p className="text-white/50 text-[10px] leading-tight">Student Portal</p>
              </div>
            </div>
            <button className="lg:hidden text-white/60 hover:text-white p-1" onClick={() => setOpen(false)}>
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Student Card */}
          <div className="px-4 pb-4">
            <div className="bg-white/10 border border-white/10 rounded-2xl p-3.5 flex items-center gap-3">
              <div className="relative flex-shrink-0">
                <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-white/30 to-white/10 flex items-center justify-center font-black text-white text-sm border border-white/20">
                  {initials}
                </div>
                {xpData ? (
                  <div className={`absolute -bottom-1 -right-1 w-5 h-5 ${levelColors.bg} rounded-full flex items-center justify-center text-[9px] font-black text-white border-2 border-[#1d4ed8] ring-1 ${levelColors.ring}`} title={`Level ${xpData.level} — ${xpData.level_name}`}>
                    {xpData.level}
                  </div>
                ) : (
                  <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-[#FACC15] rounded-full flex items-center justify-center text-[9px] font-black text-slate-800 border-2 border-[#1d4ed8]">
                    ★
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-white font-bold text-sm truncate leading-tight">{fullName}</p>
                {xpData ? (
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <span className="text-white/50 text-[11px]">{xpData.level_emoji} {xpData.level_name}</span>
                    <span className="text-white/30 text-[10px]">· {xpData.total_xp} XP</span>
                  </div>
                ) : (
                  <p className="text-white/50 text-[11px] truncate mt-0.5">{school?.name || 'Student'}</p>
                )}
              </div>
              <Bell className="w-4 h-4 text-white/40 hover:text-white cursor-pointer flex-shrink-0" />
            </div>

            {/* XP progress bar */}
            {xpData && xpData.next_level_name && (
              <div className="mt-2.5 px-1">
                <div className="w-full bg-white/10 rounded-full h-1">
                  <div
                    className="h-1 rounded-full bg-white/60 transition-all duration-500"
                    style={{ width: `${xpData.progress_pct}%` }}
                  />
                </div>
                <p className="text-white/30 text-[9px] mt-1">{xpData.xp_for_next} XP to {xpData.next_level_name}</p>
              </div>
            )}
          </div>

          {/* Streak badge */}
          {streak > 0 && (
            <div className="px-4 mb-2">
              <div className="bg-orange-500/20 border border-orange-400/30 rounded-xl px-3 py-1.5 flex items-center gap-1.5"
                data-testid="sidebar-streak-badge">
                <Flame className="w-3.5 h-3.5 text-orange-400 flex-shrink-0" />
                <span className="text-orange-300 text-[11px] font-bold">{streak} day exam prep streak!</span>
              </div>
            </div>
          )}

          <p className="px-5 text-white/40 text-[10px] font-bold uppercase tracking-widest mb-2">Navigation</p>

          {/* Nav Items */}
          <nav className="flex-1 overflow-y-auto px-3 space-y-0.5 pb-2">
            {NAV.filter(item => {
              if (!item.module) return true;
              const planModules = school?.plan_modules;
              if (!planModules || planModules.length === 0) return true;
              return planModules.includes(item.module);
            }).map(({ icon: Icon, label, path, color }) => (
              <NavLink
                key={path}
                to={path}
                onClick={() => setOpen(false)}
                className={({ isActive: a }) =>
                  `group flex items-center gap-3 px-3.5 py-2.5 rounded-2xl text-sm font-semibold transition-colors duration-200 ${
                    a
                      ? 'bg-white/20 text-white shadow-inner'
                      : 'text-white/65 hover:text-white hover:bg-white/10'
                  }`
                }
                data-testid={`student-nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 ${color} bg-white/10`}>
                  <Icon className="w-[18px] h-[18px]" />
                </div>
                <span className="flex-1">{label}</span>
              </NavLink>
            ))}
          </nav>

          {/* Logout */}
          <div className="px-3 pb-4 pt-2 border-t border-white/10">
            <button
              onClick={() => { logout(); navigate('/login'); }}
              className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-2xl text-white/50 hover:text-white hover:bg-white/10 text-sm font-semibold transition-colors"
              data-testid="student-logout-btn"
            >
              <div className="w-8 h-8 rounded-xl flex items-center justify-center bg-white/10">
                <LogOut className="w-[18px] h-[18px]" />
              </div>
              <span>Logout</span>
            </button>
          </div>
        </div>
      </aside>

      {/* ── Main Area ── */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <header className="lg:hidden h-14 bg-white/80 backdrop-blur-sm border-b border-slate-200 flex items-center gap-3 px-4 sticky top-0 z-20">
          <button onClick={() => setOpen(true)} className="p-2 rounded-xl hover:bg-slate-100 text-slate-600 transition-colors">
            <Menu className="w-5 h-5" />
          </button>
          <p className="font-black text-slate-800 text-sm tracking-wide">EDUVA</p>
          <div className="ml-auto w-8 h-8 rounded-xl bg-blue-50 flex items-center justify-center font-black text-[#2563eb] text-xs">
            {initials}
          </div>
        </header>
        <main className="flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
